import React, { SyntheticEvent, useCallback, useState } from 'react';

import { ILayoutProps } from '@/components/Layout';
import { AsaphDropdownMenu } from '@/components/shared/AsaphDropdownMenu';
import { AsaphButton } from '@/components/shared/Button';
import { EmptyResourceMessage } from '@/components/shared/EmptyResourceMessage';
import { Loader } from '@/components/shared/Loader';
import {
	emptySelectedTags,
	FilterEnum,
	getSongFieldFromFilterEnum,
	SearchAndFilterProps,
	ViewByValue,
} from '@/components/shared/SearchAndFilter';
import { SonglistPanelLayout } from '@/components/SonglistPanelLayout';
import { SortBar, SortBarProps, SortEnum } from '@/components/SortBar';
import { TeamSongCard } from '@/components/TeamSongCard';
import { songsUrl } from '@/config/urls';
import { Permissions } from '@/graphql';
import { checkRotationStatusWarning } from '@/helpers/checkRotationStatusWarning';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';
import { usePermissions } from '@/providers/PermissionsProvider';
import { useSetlists } from '@/providers/SetlistsProvider';
import { LimitedTeamSong, useTeamSongs } from '@/providers/TeamSongsProvider';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { last } from 'ramda';
import { useNavigate } from 'react-router';

import { getZonedStartDateTimeISO } from '../CreateSetlistPanel';
import styles from './SonglistModalPanel.module.scss';

export interface SonglistModalPanelProps {
	setOpen: (open: boolean) => void;
	openSong: ILayoutProps['openSong'];
	callbackFn: (songIds: string[], navigatedAway: boolean) => void;
	config?: {
		actionLabelVerb?: string;
		actionLabel?: string;
		title?: string;
		selectedTeamSongIds?: string[];
		excludeTeamSongIds?: string[];
		selectOnlyOne?: boolean;
		maxSelection?: number;
		noIndex?: boolean;
		selectedServiceId?: string | null;
	};
}

type SortAndFilterState = {
	filters: Parameters<SearchAndFilterProps['onChange']>[0];
	sort: Parameters<SortBarProps['onChange']>[0];
};

export const SonglistModalPanel: React.FC<SonglistModalPanelProps> = ({
	setOpen,
	openSong,
	callbackFn,
	config,
}) => {
	const { teamSongs, isLoading, error } = useTeamSongs();
	const { hasPermission } = usePermissions();
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();
	const { previousSetlists } = useSetlists();

	const [anchorEl, setAnchorEl] = useState<Element | null>(null);
	const [rotationStatusTooltipText, setRotationStatusTooltipText] =
		useState('');
	const [songsList, setSongsList] = useState<LimitedTeamSong[]>([]);
	const [clearFilters, setClearFilters] = useState(0);
	const [selected, setSelected] = useState<string[]>(
		config?.selectedTeamSongIds || [],
	);
	const [searchFired, setSearchFired] = useState(false);
	const [sortAndFilter, setSortAndFilter] = useState<SortAndFilterState>({
		filters: {
			searchTerm: '',
			selectedTags: emptySelectedTags,
			viewBy: 'active' as ViewByValue,
		},
		sort: SortEnum.TOP_RATED_TEAM,
	});

	const fireViewSongEvent = useAnalyticsEvent('Share_Songs_Open_View_Song');
	const fireEnterSearch = useAnalyticsEvent('Share_Songs_Enter_Search');

	const handleClose = useCallback(
		(songIds: string[] = [], navigatedAway: boolean = false) => {
			setOpen(false);
			callbackFn(songIds, navigatedAway);
			setSelected([]);
		},
		[setOpen, callbackFn],
	);

	const handleFilterChange: (sortAndFilter: SortAndFilterState) => void =
		useCallback(
			({ filters, sort }) => {
				const { searchTerm, selectedTags } = filters;
				const allTeamSongs = teamSongs;
				// This is the logic for deciding which songs to show to the user.
				setSongsList(
					// We iterate over each team song and match it based on different criteria
					allTeamSongs
						.filter((s) => {
							const excludeTeamSongIds =
								config?.excludeTeamSongIds || [];
							// If we have passed a list of team song ids to be excluded,
							// we can return the filter early as team songs with any of the
							// provided exclude ids should filtered OUT
							if (
								excludeTeamSongIds.find((eid) => eid === s.id)
							) {
								return false;
							}
							// Match song name
							const nameMatch = s.name
								.toLocaleLowerCase()
								.includes(searchTerm.toLocaleLowerCase());
							// Match artist name
							const artistMatch = s.artist
								.toLocaleLowerCase()
								.includes(searchTerm.toLocaleLowerCase());
							const artistOrNameMatch = nameMatch || artistMatch;

							const hasSelectedTags = Object.values(
								selectedTags,
							).some((tags) => !!tags.length);

							if (!hasSelectedTags) {
								// If we don't have a tagsCategory then matching the song name or artist name is enough
								return artistOrNameMatch;
							}
							// This array defines the match for each tag category
							let tagsMatched: boolean[] = [];
							// Go over every selected category and its selected tags
							for (const [tagsCategory, tags] of Object.entries(
								selectedTags,
							)) {
								// If the user HAS NOT selected any tags from this category, consider it a match
								if (!tags.length) {
									tagsMatched.push(true);
									continue;
								}
								// If the user HAS selected tags from this category then get the song's category tags
								const tagsFromCategory =
									getSongFieldFromFilterEnum(
										s,
										tagsCategory as FilterEnum,
									);
								// If any (.some in JS words) of the tags
								tagsMatched.push(
									tagsFromCategory.some((songCategoryTag) =>
										tags.includes(songCategoryTag),
									),
								);
							}

							// Return only if the name and the tags match
							return (
								artistOrNameMatch &&
								tagsMatched.every((match) => match)
							);
						})
						.sort((a, b) => {
							const aOverall = a.feedback?.overallScore || 0;
							const bOverall = b.feedback?.overallScore || 0;
							const aLastPlayed = new Date(
								a.feedback?.teamSongFeedbackReports.slice(-1)[0]
									?.startAt || 0,
							).getTime();
							const bLastPlayed = new Date(
								b.feedback?.teamSongFeedbackReports.slice(-1)[0]
									?.startAt || 0,
							).getTime();

							switch (sort) {
								case SortEnum.TOP_RATED_TEAM:
									if (aOverall === bOverall) {
										return 0;
									}
									return aOverall > bOverall ? -1 : 1;
								case SortEnum.LAST_PLAYED:
									if (aLastPlayed === bLastPlayed) {
										return 0;
									}
									return aLastPlayed > bLastPlayed ? -1 : 1;
								default:
									if (a.name === b.name) {
										return 0;
									}
									return a.name > b.name ? 1 : -1;
							}
						}),
				);
				if (!searchFired && searchTerm) {
					fireEnterSearch();
					setSearchFired(true);
				}
			},
			[
				config?.excludeTeamSongIds,
				fireEnterSearch,
				searchFired,
				teamSongs,
			],
		);

	const handleSortAndFilter: (
		filters?: Parameters<SearchAndFilterProps['onChange']>[0],
		sort?: Parameters<SortBarProps['onChange']>[0],
	) => void = useCallback(
		(filters, sort) => {
			setSortAndFilter((state) => {
				const newSortAndFilter = {
					filters: filters || state.filters,
					sort: sort || state.sort,
				};
				handleFilterChange(newSortAndFilter);
				return newSortAndFilter;
			});
		},
		[handleFilterChange],
	);

	const searchOnChange: SearchAndFilterProps['onChange'] = useCallback(
		(filters) => {
			handleSortAndFilter(filters);
		},
		[handleSortAndFilter],
	);

	const sortBarOnChange: SortBarProps['onChange'] = useCallback(
		(sort) => {
			handleSortAndFilter(undefined, sort);
		},
		[handleSortAndFilter],
	);

	const handleSubmit = () => {
		handleClose(selected);
	};

	const handleClear = () => {
		setSelected([]);
	};

	const handleCardSelect = useCallback(
		(songId: string) => {
			if (selected.includes(songId)) {
				setSelected(selected.filter((id) => id !== songId));
			} else {
				if (config?.selectOnlyOne) {
					return setSelected([songId]);
				}
				if (config?.maxSelection !== undefined && selected.length >= config.maxSelection) {
					return;
				}
				setSelected([...selected, songId]);
			}
		},
		[config?.selectOnlyOne, config?.maxSelection, selected],
	);

	const handleRotationStatusOpen = (e: SyntheticEvent) => {
		setAnchorEl(e.currentTarget);
	};
	const handleRotationStatusClose = () => {
		setAnchorEl(null);
	};

	if (isLoading) {
		return <Loader />;
	}

	if (error || !teamSongs) {
		console.error(error);
		enqueueSnackbar('Error while loading songs', { variant: 'error' });
	}

	const noSongs = !teamSongs.length;
	const rotationStatusOpen = !!anchorEl;
	const isAtMax = config?.maxSelection !== undefined && selected.length >= config.maxSelection;

	return (
		<SonglistPanelLayout
			open={true}
			handleClose={handleClose}
			handleFilterChange={searchOnChange}
			clearFilters={clearFilters}
			title={config?.title || 'Share songs'}
			actionButton={
				selected.length > 0 ? (
					<div style={{ display: 'flex', gap: '10px' }}>
						<AsaphButton
							color="grey"
							label="Clear"
							onClick={handleClear}
							fullWidth
						/>
						<AsaphButton
							color="blue"
							label={config?.actionLabel || `${config?.actionLabelVerb || `Share`}${
								selected.length > 1 ? ` ${selected.length}` : ``
							} song${selected.length > 1 ? 's' : ''}`}
							onClick={handleSubmit}
							fullWidth
							disabled={config?.maxSelection !== undefined && !isAtMax}
						/>
					</div>
				) : undefined
			}
			listClassName={
				noSongs || !songsList.length ? styles['list-full-height'] : ''
			}
		>
			{noSongs ? (
				hasPermission(Permissions.TeamUpdate) ? (
					<EmptyResourceMessage
						onActionClick={() => {
							handleClose([], true);
							navigate(songsUrl);
						}}
						title="There are no songs"
						subtitle="Add songs to create your Songlist"
						actionLabel="Add Songs"
					/>
				) : (
					<EmptyResourceMessage
						title="There are no songs"
						subtitle="Songs will appear here when admins add them to your Songlist"
					/>
				)
			) : songsList.length ? (
				<div>
					<SortBar onChange={sortBarOnChange} />
					{songsList.map((teamSong) => {
						const checked = selected.indexOf(teamSong.id);

						const teamSongLastPlayedAt = (teamSong?.setlists || [])
							.reduce<string[]>((acc, s) => {
								const setlist = previousSetlists.find(
									(ps) =>
										ps.id === s.id &&
										ps.serviceId ===
											config?.selectedServiceId,
								);
								if (setlist) {
									return [...acc, setlist.startAt];
								}
								return acc;
							}, [])
							.splice(-2); // Get the last two setlists where this song was used
						const lastPlayedAt = last(teamSongLastPlayedAt); // Get the last time the song was played
						const comparisonISODate = getZonedStartDateTimeISO();

						const rotationStatusWarning =
							!!teamSong &&
							checkRotationStatusWarning({
								lastPlayedAt: teamSongLastPlayedAt,
								rotationStatus: teamSong.rotationStatus,
								comparisonISODate,
							});
						return (
							// There isn't much of a difference if we use TeamSongCard
							// or SongCard here as we have all the data available.
							// TeamSongCard howerver opens the modal by consuming the LayoutContext.
							// In this case it would be empty as the SonglistModalPanel is defined outside
							// the context provider. We need to call the `openSong` manually here and pass
							// all the details for the song that we need
							<TeamSongCard
								key={teamSong.id}
								teamSongId={teamSong.id}
								onClick={() => {
									openSong({
										teamSongId: teamSong.id,
										viewOnly: false,
									});
									fireViewSongEvent({
										id: teamSong.id,
										artist: teamSong.artist,
										title: teamSong.name,
									});
								}}
								onSelect={() => handleCardSelect(teamSong.id)}
								selectable
								checked={checked !== -1}
								isDisabled={isAtMax && checked === -1}
								index={
									!config?.noIndex ? checked + 1 : undefined
								}
								negativeSpacing
								displayTrend={
									sortAndFilter.sort !== SortEnum.LAST_PLAYED
								}
								displayOverallScore={
									sortAndFilter.sort ===
									SortEnum.TOP_RATED_TEAM
								}
								displayKey={
									sortAndFilter.sort === SortEnum.ALPHANUMERIC
								}
								displayLastPlayed={
									sortAndFilter.sort === SortEnum.LAST_PLAYED
								}
								displayRotationStatus={
									sortAndFilter.sort === SortEnum.LAST_PLAYED
								}
								rotationStatusWarning={!!rotationStatusWarning}
								rotationStatusOnClick={(e) => {
									if (rotationStatusWarning) {
										handleRotationStatusOpen(e);
										setRotationStatusTooltipText(
											`${rotationStatusWarning} ${
												lastPlayedAt
													? `It was last played on ${format(
															new Date(
																lastPlayedAt,
															),
															'dd MMMM yyyy',
													  )}.`
													: ``
											}`,
										);
									}
								}}
							/>
						);
					})}
					<AsaphDropdownMenu
						items={[]}
						customContent={
							<div className={styles['tooltip']}>
								{rotationStatusTooltipText}
							</div>
						}
						anchorEl={anchorEl}
						open={rotationStatusOpen}
						onClose={handleRotationStatusClose}
					/>
				</div>
			) : (
				<EmptyResourceMessage
					onActionClick={() => setClearFilters(clearFilters + 1)}
					title="No songs"
					subtitle="Your filters have returned 0 results"
					actionLabel="Clear filters"
				/>
			)}
		</SonglistPanelLayout>
	);
};
